import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@material-ui/core";
import { Resource } from "react-admin";
import { useTokenOps } from "../components/TokenContext";
import { hostname } from "../providers/api";
import { carbonRequest } from "./corporateCarbon/api";
import { triggerBlobDownload } from "./corporateCarbon/downloadFilename";
import {
  CarbonAdminPage,
  CarbonDataTable,
  CarbonSection,
  CarbonSelectInput,
} from "./corporateCarbon/CarbonAdminPage";
import { carbonFileSize, friendlyCarbonError, reportingRevisionStatusLabel as reportingRevisionLabel } from "./corporateCarbon/workflow";

export const exportFormatLabel = (format) => ({ xlsx: "Excel", pdf: "PDF" }[format] || format);

const completedAtLabel = (value) => value ? new Date(value).toLocaleString() : "Not recorded";
const byteSizeLabel = carbonFileSize;
const exportStatusLabel = (status) => ({
  processing: "Generating",
  ready: "Ready",
  failed: "Generation failed",
}[status] || "Ready");
const wait = (milliseconds) => new Promise((resolve) => setTimeout(
  resolve,
  process.env.NODE_ENV === "test" ? 0 : milliseconds,
));

export const CarbonExportPage = () => {
  const token = useTokenOps().account?.accessToken;
  const generation = useRef(0);
  const [runs, setRuns] = useState([]);
  const [selectedRun, setSelectedRun] = useState(null);
  const [format, setFormat] = useState("xlsx");
  const [items, setItems] = useState([]);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [generationStatus, setGenerationStatus] = useState("");

  const refreshHistory = useCallback(async (current = generation.current) => {
    const rows = await carbonRequest(token, "corporate-carbon/exports");
    if (current === generation.current) setItems(rows || []);
    return rows || [];
  }, [token]);

  const refresh = useCallback(async (current = generation.current) => {
    const [runPage, rows] = await Promise.all([
      carbonRequest(token, "corporate-carbon/calculations/exportable?page=1&limit=100"),
      carbonRequest(token, "corporate-carbon/exports"),
    ]);
    if (current === generation.current) {
      setRuns(runPage.items || runPage.data || []);
      setItems(rows || []);
    }
  }, [token]);

  useEffect(() => {
    const current = ++generation.current;
    setRuns([]);
    setItems([]);
    setSelectedRun(null);
    setFormat("xlsx");
    setBusy(false);
    setError("");
    setGenerationStatus("");
    if (token) refresh(current).catch((requestError) => {
      if (current === generation.current) setError(friendlyCarbonError(requestError));
    });
    return () => { generation.current += 1; };
  }, [token, refresh]);

  useEffect(() => {
    if (!token || busy || !items.some((item) => item.status === "processing")) return undefined;
    const current = generation.current;
    const timer = setTimeout(() => {
      refreshHistory(current).catch((requestError) => {
        if (current === generation.current) setError(friendlyCarbonError(requestError));
      });
    }, 2000);
    return () => clearTimeout(timer);
  }, [busy, items, refreshHistory, token]);

  const create = async () => {
    if (!selectedRun || busy) return;
    const current = generation.current;
    setBusy(true);
    try {
      setError("");
      const created = await carbonRequest(token, "corporate-carbon/exports", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ runId: selectedRun.id, format }),
      });
      if (current === generation.current) {
        setGenerationStatus("Generating export…");
        let status = created.status || "processing";
        let attempts = 0;
        while (status === "processing" && current === generation.current && attempts < 90) {
          const rows = await refreshHistory(current);
          status = rows.find((item) => item.id === created.id)?.status || "processing";
          attempts += 1;
          if (status === "processing") await wait(2000);
        }
        if (current !== generation.current) return;
        if (status === "processing") throw new Error("Export generation is taking longer than expected. Its status will continue updating in export history.");
        if (status === "failed") throw new Error("Export generation failed. Please try again.");
        setSelectedRun(null);
        setFormat("xlsx");
        setGenerationStatus("");
      }
    } catch (requestError) {
      if (current === generation.current) {
        setGenerationStatus("Generation failed");
        setError(friendlyCarbonError(requestError));
      }
    } finally {
      if (current === generation.current) setBusy(false);
    }
  };

  const download = async (item) => {
    const current = generation.current;
    try {
      setError("");
      const response = await fetch(
        `${hostname}/corporate-carbon/exports/${item.id}/download`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (current !== generation.current) return;
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.message || body.code || "Export download failed");
      }
      const blob = await response.blob();
      if (current !== generation.current) {
        return;
      }
      triggerBlobDownload(blob, response, {
        displayFilename: item.evidenceDocument?.displayFilename || "corporate-carbon-export",
        extension: `.${item.format}`,
      });
    } catch (requestError) {
      if (current === generation.current) setError(friendlyCarbonError(requestError));
    }
  };

  return (
    <CarbonAdminPage
      title="Carbon exports"
      description="Download a report for a completed, finalized reporting revision."
    >
      {error && <Typography color="error" role="alert">{error}</Typography>}
      <CarbonSection
        title="Reports ready to export"
        description="Choose a reporting revision and then choose Excel or PDF."
      >
        {runs.length ? (
          <CarbonDataTable label="Successful calculation runs">
            <thead><tr><th>Reporting year</th><th>Reporting revision</th><th>Factor release</th><th>Algorithm</th><th>Completed</th><th>Actions</th></tr></thead>
            <tbody>{runs.map((run) => (
              <tr key={run.id}>
                <td>{run.reportingYear}</td>
                <td>{reportingRevisionLabel(run)}</td>
                <td>{run.factorReleaseVersion || 'Not recorded'}</td>
                <td>{run.algorithmVersion || 'Not recorded'}</td>
                <td>{completedAtLabel(run.completedAt)}</td>
                <td>
                  <Button aria-label={`Export run ${run.id}`} color="primary" onClick={() => { setSelectedRun(run); setFormat("xlsx"); }}>Export</Button>
                </td>
              </tr>
            ))}</tbody>
          </CarbonDataTable>
        ) : <Typography color="textSecondary">No successful calculation runs are available to export.</Typography>}
      </CarbonSection>
      <CarbonSection title="Export history">
        {items.length ? (
          <CarbonDataTable label="Export history">
            <thead><tr><th>File</th><th>Reporting revision</th><th>Format</th><th>Status</th><th>Generated</th><th>Generated by</th><th>Calculation</th><th>Size</th><th>Actions</th></tr></thead>
            <tbody>{items.map((item) => <tr key={item.id}><td>{item.evidenceDocument?.displayFilename || "Carbon export"}</td><td>{reportingRevisionLabel(item)}</td><td>{exportFormatLabel(item.format)}</td><td>{item.revisionStatus === 'superseded' ? 'Historical · Superseded' : exportStatusLabel(item.status)}</td><td>{completedAtLabel(item.createdAt)}</td><td>{item.generatedBy || 'Not recorded'}</td><td><a href={`#/carbonResults?revisionId=${item.revisionId}&runId=${item.runId}`}>{completedAtLabel(item.completedAt)} · Factors {item.releaseVersion}</a></td><td>{byteSizeLabel(item.evidenceDocument?.byteSize)}</td><td>{(item.status || "ready") === "ready" ? <Button aria-label={`Download ${item.format.toUpperCase()} export for run ${item.runId}`} color="primary" onClick={() => download(item)}>Download</Button> : null}</td></tr>)}</tbody>
          </CarbonDataTable>
        ) : <Typography color="textSecondary">No exports have been generated.</Typography>}
      </CarbonSection>
      <Dialog
        aria-labelledby="carbon-export-dialog-title"
        BackdropProps={process.env.NODE_ENV === "test" ? { transitionDuration: 0 } : undefined}
        open={Boolean(selectedRun)}
        onClose={busy ? undefined : () => setSelectedRun(null)}
        transitionDuration={process.env.NODE_ENV === "test" ? 0 : undefined}
      >
        <DialogTitle id="carbon-export-dialog-title">Export calculation run</DialogTitle>
        <DialogContent>
          {generationStatus ? <Typography role="status">{generationStatus}</Typography> : null}
          {selectedRun ? <Typography>Factors {selectedRun.factorReleaseVersion || 'Not recorded'} · Algorithm {selectedRun.algorithmVersion || 'Not recorded'} · Calculated {completedAtLabel(selectedRun.completedAt)}</Typography> : null}
          <Typography>{selectedRun ? `${selectedRun.reportingYear} · ${reportingRevisionLabel(selectedRun)}` : ""}</Typography>
          <CarbonSelectInput
            choices={[{ id: "xlsx", name: "Excel" }, { id: "pdf", name: "PDF" }]}
            emptyText={null}
            label="Output format"
            name="outputFormat"
            onChange={(event) => setFormat(event.target.value)}
            value={format}
          />
        </DialogContent>
        <DialogActions>
          <Button disabled={busy} onClick={() => setSelectedRun(null)}>Cancel</Button>
          <Button color="primary" disabled={busy} onClick={create} variant="contained">
            {busy ? "Generating…" : "Generate export"}
          </Button>
        </DialogActions>
      </Dialog>
    </CarbonAdminPage>
  );
};

const resource = () => (
  <Resource name="carbonExports" options={{ label: "Exports", menuGroup: "Carbon Accounting" }} list={CarbonExportPage} />
);
export default resource;
